"use client";

import { useState } from "react";
import {
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import type { Strings } from "@/components/tools/price-index-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type BasketItem,
  type BasketResult,
  badItemIndex,
  basketIndexes,
  validBasket,
} from "@/lib/tool-kit/econ-macro/price-index";

interface Row {
  id: number;
  name: string;
  basePrice: string;
  baseQuantity: string;
  currentPrice: string;
  currentQuantity: string;
}

const MAX_ITEMS = 12;

function startRows(strings: Strings): Row[] {
  return [
    {
      id: 1,
      name: strings.sampleBread,
      basePrice: "2",
      baseQuantity: "50",
      currentPrice: "2.1",
      currentQuantity: "48",
    },
    {
      id: 2,
      name: strings.sampleMilk,
      basePrice: "3",
      baseQuantity: "40",
      currentPrice: "3.12",
      currentQuantity: "40",
    },
    {
      id: 3,
      name: strings.sampleGas,
      basePrice: "3",
      baseQuantity: "100",
      currentPrice: "3.3",
      currentQuantity: "92",
    },
    {
      id: 4,
      name: strings.sampleMovie,
      basePrice: "10",
      baseQuantity: "5",
      currentPrice: "10.5",
      currentQuantity: "6",
    },
  ];
}

type Analysis =
  | { items: BasketItem[]; badRow: null }
  | { items: null; badRow: number };

function readRows(rows: Row[], itemName: (n: number) => string): Analysis {
  const items: BasketItem[] = [];
  for (const [index, row] of rows.entries()) {
    const parsed = [
      row.basePrice,
      row.baseQuantity,
      row.currentPrice,
      row.currentQuantity,
    ].map(parseNumberInput);
    const [basePrice, baseQuantity, currentPrice, currentQuantity] = parsed;
    if (
      basePrice === null ||
      baseQuantity === null ||
      currentPrice === null ||
      currentQuantity === null
    ) {
      return { items: null, badRow: index + 1 };
    }
    items.push({
      name: row.name.trim() || itemName(index + 1),
      basePrice,
      baseQuantity,
      currentPrice,
      currentQuantity,
    });
  }
  return { items, badRow: null };
}

function BasketForm({
  rows,
  setRows,
  nextId,
  setNextId,
  strings,
}: {
  rows: Row[];
  setRows: (update: (old: Row[]) => Row[]) => void;
  nextId: number;
  setNextId: (id: number) => void;
  strings: Strings;
}) {
  const edit = (id: number, key: keyof Row) => (value: string) =>
    setRows((old) =>
      old.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  return (
    <div className="space-y-4">
      {rows.map((row, index) => (
        <fieldset
          className="space-y-3 rounded-lg border border-border p-4"
          key={row.id}
        >
          <legend className={`${LABEL} px-1`}>
            {t(strings.itemHeading, { n: String(index + 1) })}
          </legend>
          <TextField
            id={`pi-name-${row.id}`}
            label={strings.itemName}
            onChange={edit(row.id, "name")}
            placeholder={t(strings.itemDefault, { n: String(index + 1) })}
            value={row.name}
          />
          <div className="grid gap-4 sm:grid-cols-4">
            <TextField
              id={`pi-p0-${row.id}`}
              label={strings.basePrice}
              numeric
              onChange={edit(row.id, "basePrice")}
              value={row.basePrice}
            />
            <TextField
              id={`pi-q0-${row.id}`}
              label={strings.baseQuantity}
              numeric
              onChange={edit(row.id, "baseQuantity")}
              value={row.baseQuantity}
            />
            <TextField
              id={`pi-p1-${row.id}`}
              label={strings.currentPrice}
              numeric
              onChange={edit(row.id, "currentPrice")}
              value={row.currentPrice}
            />
            <TextField
              id={`pi-q1-${row.id}`}
              label={strings.currentQuantity}
              numeric
              onChange={edit(row.id, "currentQuantity")}
              value={row.currentQuantity}
            />
          </div>
          {rows.length > 1 ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                setRows((old) => old.filter((other) => other.id !== row.id))
              }
              type="button"
            >
              {t(strings.removeItem, { n: String(index + 1) })}
            </button>
          ) : null}
        </fieldset>
      ))}
      {rows.length < MAX_ITEMS ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((old) => [
              ...old,
              {
                id: nextId,
                name: "",
                basePrice: "",
                baseQuantity: "",
                currentPrice: "",
                currentQuantity: "",
              },
            ]);
            setNextId(nextId + 1);
          }}
          type="button"
        >
          {strings.addItem}
        </button>
      ) : null}
    </div>
  );
}

function BasketResults({
  result,
  currency,
  strings,
}: {
  result: BasketResult;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const index = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const change = (value: number) =>
    `${fmt(value - 100, { maximumFractionDigits: 2 })}%`;
  const num = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  const values = {
    a: money(result.baseBasketCurrent),
    b: money(result.baseBasketBase),
    c: money(result.currentBasketCurrent),
    d: money(result.currentBasketBase),
    l: num(result.laspeyres),
    p: num(result.paasche),
    f: num(result.fisher),
    change: change(result.laspeyres),
  };
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.laspeyres}
          value={index(result.laspeyres)}
        />
        <StatCard label={strings.paasche} value={index(result.paasche)} />
        <StatCard label={strings.fisher} value={index(result.fisher)} />
        <StatCard
          label={strings.inflationLaspeyres}
          value={change(result.laspeyres)}
        />
        <StatCard
          label={strings.inflationPaasche}
          value={change(result.paasche)}
        />
        <StatCard
          label={strings.inflationFisher}
          value={change(result.fisher)}
        />
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.costHeading}</h3>
      <DataTable
        head={[
          strings.basketColumn,
          strings.atBaseColumn,
          strings.atCurrentColumn,
        ]}
        rows={[
          [
            strings.rowBaseBasket,
            money(result.baseBasketBase),
            money(result.baseBasketCurrent),
          ],
          [
            strings.rowCurrentBasket,
            money(result.currentBasketBase),
            money(result.currentBasketCurrent),
          ],
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.itemsHeading}</h3>
      <DataTable
        head={[
          strings.goodColumn,
          strings.relativeColumn,
          strings.shareColumn,
          strings.contributionColumn,
        ]}
        rows={result.items.map((item) => [
          item.name,
          fmt(item.priceRelative, { maximumFractionDigits: 4 }),
          `${fmt(item.baseShare * 100, { maximumFractionDigits: 2 })}%`,
          `${fmt(item.contribution * 100, { maximumFractionDigits: 2 })}%`,
        ])}
      />
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className={line}>{t(strings.workLaspeyres, values)}</p>
        <p className={line}>{t(strings.workPaasche, values)}</p>
        <p className={line}>{t(strings.workFisher, values)}</p>
        <p className={line}>{t(strings.workChange, values)}</p>
      </div>
      <Notice>{strings.compareNote}</Notice>
    </div>
  );
}

/** The basket mode: a list of goods and the three price indexes they give. */
export function BasketMode({ strings }: { strings: Strings }) {
  const [currency, setCurrency] = useState("USD");
  const [rows, setRowsState] = useState<Row[]>(() => startRows(strings));
  const [nextId, setNextId] = useState(5);
  const analysis = readRows(rows, (n) =>
    t(strings.itemDefault, { n: String(n) })
  );
  const items = analysis.items;
  const valid = items !== null && validBasket(items);
  const result = valid ? basketIndexes(items) : null;
  let problem: string | null = null;
  if (!valid) {
    const bad = items ? badItemIndex(items) : analysis.badRow - 1;
    problem =
      bad >= 0
        ? t(strings.invalidBasket, { n: String(bad + 1) })
        : strings.emptyBasket;
  }
  return (
    <>
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.basketHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.basketHint}</p>
        <CurrencyField
          id="pi-currency"
          label={strings.currency}
          onChange={setCurrency}
          plainLabel={strings.plainNumbers}
          value={currency}
        />
        <BasketForm
          nextId={nextId}
          rows={rows}
          setNextId={setNextId}
          setRows={(update) => setRowsState(update)}
          strings={strings}
        />
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {result ? (
          <BasketResults
            currency={currency}
            result={result}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}
